/* eslint-disable consistent-return, no-void */
import moment from 'moment';
import { mapActions, mapGetters, mapState } from 'vuex';
import { authorizeApi } from '@/services/api/http';
import notificationProxy from '@/services/notifications/proxy';
import mobileWrapper from '@/services/mobile/wrapper';
import overlay from '@/components/generics/overlay/overlay.vue';
import appLayoutOld from '@/components/app/app-layout/app-layout.vue';
import accountVerifyMixin from '@/mixins/accountVerify';
import constants from '@/constants';
import log from '@/logger';
import ls from '@/helpers/localStorage';
import appModals from '@/components/app/app-modals/app-modals.vue';
import appPrivacyStatement from '@/components/app/app-privacy-statement/app-privacy-statement.vue';
import { MODALS } from '@/components/app/app-modals/app-modals';
import instantCreditService from '@/services/instant-credit';
import { loadScript } from '@/utils/helpers';
import appLoader from '@/components/app/app-loader/app-loader.vue';
import loaderMixin from '@/mixins/loader';
import userAgentMixin from '@/mixins/userAgent';

const RESIZE_HANDLER_TIMEOUT = 500;
const SOCIAL_AUTH_ROUTES = [
  'disqus-auth',
  'social-auth',
];

export default {
  mixins: [
    accountVerifyMixin,
    userAgentMixin,
    loaderMixin,
  ],
  data() {
    return {
      appFlowDisabled: false,
      appInitialPopupShown: false,
      initialised: false,
      disconnectTimeout: null,
      connectTimeout: null,
      connectTimeoutAnonymous: null,
      touchStartTop: 0,
    };
  },
  components: {
    overlay,
    appPrivacyStatement,
    appLayoutOld,
    appModals,
    appLoader,
  },
  computed: {
    ...mapState('app', ['skipAttachToHomeNotification', 'isTabActive', 'newLoaderCounter']),
    ...mapState('user', ['token']),
    ...mapState('market', ['activeCategory']),
    ...mapState('popup', ['currentPopup']),
    ...mapState('profile', ['profileStatus']),
    ...mapGetters('user', ['loginWithDisqus']),
    ...mapGetters('app', ['hasMaintenance', 'isLayoutMobile', 'isShutdownModeOn', 'newLoaderIsLoading']),
    ...mapState('user', ['navigatingToTwoFactorSetup']),
    layoutComponent() {
      return appLayoutOld;
    },
  },
  methods: {
    ...mapActions('app', ['setLayoutWidth', 'setIsTabActive', 'setLiveUpdatesDisabled', 'setShowOdds']),
    ...mapActions('browse', ['getHeaders']),
    ...mapActions('user', [
      'getStatus', 'getAccountDetail', 'getUserInfo', 'login', 'logout',
      'setBareToken', 'setUserUnreadNotificationsCount', 'checkAndCacheTwoFactorStatus',
    ]),
    ...mapActions('profile', ['getProfileDetail', 'getAllSettings', 'setProfileStatus']),
    ...mapActions('wallet', ['getBalance', 'clearBalance']),
    ...mapActions('market', ['getBiggestGains']),
    ...mapActions('popup', ['setCurrentPopup', 'setCurrentPopupOptions']),
    getUserDetailedInformation() {
      this.getAccountDetail()
        .then((data) => {
          this.setUserUnreadNotificationsCount(data.userUnreadNotificationsCount);
          if (data.idVerified) {
            return this.getBalance();
          }
          return Promise.resolve(1);
        })
        .then(this.getAllSettings)
        .then(this.getStatus)
        .then((data) => {
          this.appInitialPopupShown = false;
          if (data && data.isClassicEnabled === false) {
            this.appFlowDisabled = true;
            this.appInitialPopupShown = true;
            this.setCurrentPopup(MODALS.ACCOUNT_ENABLE_CLASSIC);
          }
          const skipRoutes = ['terms-and-conditions', 'password-set'];
          const showVerifyPopup = !skipRoutes.includes(this.$route.name) &&
            !this.hasMaintenance &&
            !this.checkDataIfUserIsVerified(this.userData);

          if (showVerifyPopup && !this.appInitialPopupShown) {
            this.showAccountVerifyPopup();
            // Stop here - let modal close handler continue the flow
            return;
          } else if (!this.appInitialPopupShown) {
            this.showAttachToHomeNotification();
          }
          if (this.appFlowDisabled) {
            return Promise.resolve(1);
          }
          // Cache 2FA status for modal chain decisions (must complete before modals show)
          return this.checkAndCacheTwoFactorStatus().then(() => {
            // Guard: skip ADD_FUNDS if navigating to 2FA setup or already on settings page
            const skipAddFunds = this.navigatingToTwoFactorSetup ||
              this.$route.name === 'account-settings';

            if (this.userData.idVerified) {
              if (
                !this.hasMaintenance &&
                !this.appInitialPopupShown &&
                this.balance.accountBalanceDecimal === 0 &&
                !this.currentPopup &&
                !this.$route.query.coinbase && // dont show upon returning from coinbase
                this.$route.name !== 'wallet' &&
                !instantCreditService.checkIfCanRunAfterLogin() && // will be running code check
                !skipAddFunds
              ) {
                this.appInitialPopupShown = true;
                // disabled on purpose
                // this.setCurrentPopup(MODALS.ADD_FUNDS);
              } else if (
                // Fallback: if no ADD_FUNDS shown, check if we should show 2FA prompt directly
                !this.appInitialPopupShown &&
                !this.currentPopup &&
                this.$store.state.user.twoFactorEnabled !== true &&
                !constants.DISABLE_TWO_FACTOR_PROMPT
              ) {
                // Verified user with positive balance and no 2FA - show prompt directly
                this.appInitialPopupShown = true;
                this.setCurrentPopup(MODALS.TWO_FACTOR_PROMPT);
              }
              this.routeQueryHandler();
            } else {
              const showAffiliationPopup = this.userData.idVerified
                && data.isUCAffilSet === null
                && !this.appInitialPopupShown;
              if (showAffiliationPopup) {
                this.appInitialPopupShown = true;
                this.setCurrentPopup(MODALS.AFFILIATIONS);
              }
            }
            return { skipAddFunds }; // Pass to next .then() for use in instant credit checks
          });
        })
        .then(this.getProfileDetail)
        .then((data) => {
          if (this.appFlowDisabled) {
            return Promise.resolve(1);
          }
          if (ls.get('registeredExternal', false)) {
            ls.del('registeredExternal');
            this.$gtm.trackEvent({
              event: 'SignUp',
              gtmUserID: data.userGuid,
              otmUserID: data.otmUserId,
              email: this.userData.email,
            });
          } else if (this.profileStatus.event && this.profileStatus.event === 'registered') {
            this.$gtm.trackEvent({
              event: 'SignUp',
              gtmUserID: data.userGuid,
              otmUserID: data.otmUserId,
              email: this.userData.email,
            });
            this.setProfileStatus({});
          } else if (this.profileStatus.event && this.profileStatus.event === 'login') {
            this.$gtm.trackEvent({ event: 'Login' });
            this.setProfileStatus({});
            // Guard: skip ADD_FUNDS if navigating to 2FA setup
            const skipAddFunds = this.navigatingToTwoFactorSetup ||
              this.$route.name === 'account-settings';
            if (instantCreditService.checkIfCanRunAfterLogin() && !skipAddFunds) {
              // Firing quick deposit with instant credit check
              this.appInitialPopupShown = true;
              this.setCurrentPopup(MODALS.ADD_FUNDS);
              this.setCurrentPopupOptions({
                checkInstantCredit: true,
              });
            }
          } else if (instantCreditService.checkIfCanRunAfterLogin()) {
            // Guard: skip ADD_FUNDS if navigating to 2FA setup
            const skipAddFunds = this.navigatingToTwoFactorSetup ||
              this.$route.name === 'account-settings';
            if (!skipAddFunds) {
              // Firing quick deposit with instant credit check
              this.appInitialPopupShown = true;
              this.setCurrentPopup(MODALS.ADD_FUNDS);
              this.setCurrentPopupOptions({
                checkInstantCredit: true,
              });
            }
          }
        });
    },
    getUserData() {
      if (!this.token) return;

      if (SOCIAL_AUTH_ROUTES.includes(this.$route.name)) {
        this.getUserInfo()
          .then((response) => {
            if (response.data.hasRegistered) {
              this.getUserDetailedInformation();
            }
          });
      } else {
        this.getUserDetailedInformation();
      }
    },
    storageHandler() {
      const storageToken = ls.get('token', null);
      // token removed from localstorage (user logged out in other tab/window)
      if (this.token && !storageToken) {
        this.logout();
        // token added to localstorage and it's different --> update app state
      } else if (this.token && this.token !== storageToken) {
        this.setBareToken(storageToken);
      }
    },
    resizeHandler() {
      setTimeout(() => {
        const width = window.innerWidth || document.documentElement.clientWidth || document.getElementsByTagName('body')[0].clientWidth;
        this.setLayoutWidth(width);
      }, RESIZE_HANDLER_TIMEOUT);
    },
    orientationChangeHandler() {
      setTimeout(() => {
        if (this.isLayoutMobile && this.getOrientation() === 'landscape' && !this.isTablet) {
          this.setCurrentPopup(MODALS.ORIENTATION);
        } else if (this.currentPopup === MODALS.ORIENTATION) {
          this.setCurrentPopup();
        }
      }, 10);
    },
    touchStartHandler(event) {
      if (!this.isIos) return;
      if (event.touches && event.touches[0]) {
        this.touchStartTop = event.touches[0].pageY;
      }
    },
    touchMoveHandler(event) {
      if (!this.isIos) return;
      if (!event.touches || !event.touches[0]) return;

      const currentY = event.touches[0].pageY;
      const diffY = currentY - this.touchStartTop;

      if (window.pageYOffset === 0 && diffY > 30) {
        // Trigger a tiny scroll to force Safari to re-evaluate the viewport
        void document.body.offsetHeight;
        window.scrollBy(0, 1);
        window.scrollBy(0, -1);
      }
    },
    refreshAccessToken() {
      if (!this.token) return;
      const expiresAt = ls.get('tokenExpires', null);
      const threshold = -parseInt(process.env.REFRESH_TOKEN_INTERVAL, 10);
      const hasExpired = (expiresAt === null || moment().diff(moment(expiresAt), 'seconds') > threshold);
      // no expires at or expires in less than a minute
      if (hasExpired) {
        const payload = {
          grant_type: 'refresh_token',
          refresh_token: ls.get('refreshToken'),
        };
        this.login(payload)
          .catch(() => this.logout());
      }
    },
    accessTokenRenewal() {
      clearInterval(this.tokenInterval);
      this.tokenInterval = setInterval(() => {
        this.refreshAccessToken();
      }, parseInt(process.env.REFRESH_TOKEN_INTERVAL, 10) * 1000);
    },
    showAttachToHomeNotification() {
      // if (
      //   !this.token ||
      //   this.skipAttachToHomeNotification ||
      //   !this.isIos ||
      //   this.isStandalone ||
      //   this.currentPopup ||
      //   this.iOSWrapper ||
      //   !process.env.ATTACH_TO_HOME_POPUP_ENABLED ||
      //   !this.isLayoutMobile
      // ) return;
      // setTimeout(
      //   () => this.setCurrentPopup(MODALS.ATTACH_TO_HOME),
      //   constants.DEFAULT_SMALL_POPUP_TIMEOUT);
    },
    setupUI() {
      document.body.classList.remove('splash');
      if (this.isStandalone) document.body.classList.add('standalone');
      if (this.isIos) {
        document.documentElement.classList.add('ios');
        document.body.classList.add('ios');
      }
      if (this.isIE) {
        document.body.classList.add('ie');
      }
      if (this.isEdge) {
        document.body.classList.add('edge');
      }
      if (this.isWrapperApp) {
        document.body.classList.add('wrapper');
      }
      if (this.isShutdownModeOn) {
        document.body.classList.add('shutdown');
      }
    },
    pwaRedirect() {
      const routePath = ls.get('currentRoutePath', null);
      if (this.isStandalone && routePath && routePath !== '/') {
        this.$router.push(routePath);
      } else if (this.isStandalone && routePath === null) {
        this.$router.push({ name: 'home' });
      }
    },
    routeQueryHandler() {
      if (this.$route.query.coinbase === 'success') {
        this.$router.push({ name: 'market-list' });
      }
      const hasQuickDeposit = (this.$route.query.quickdeposit && this.$route.query.quickdeposit === 'true') ||
        (this.$route.query.quick_deposit && this.$route.query.quick_deposit === 'true');
      if (this.popupShown) {
        return;
      }
      if (this.$route.name !== 'wallet' && hasQuickDeposit && this.isDepositEnabled) {
        this.setCurrentPopup(MODALS.ADD_FUNDS);
        this.popupShown = true;
      }
    },
    initializeUserData() {
      // when user comes from social login we need to fetch data manually
      // because he doesnt have refreshToken witch causes
      // token watch to trigger
      if (ls.get('refreshToken', null) && !SOCIAL_AUTH_ROUTES.includes(this.$route.name)) {
        this.refreshAccessToken();
      }
    },
    tokenHandler(newToken) {
      const secondsToDelay = parseInt(process.env.SECONDS_TO_ENABLE_WEBSOCKETS, 10);

      if (this.initialised && !newToken) {
        const routeRequiresAuth = (this.$route && this.$route.matched) ?
          this.$route.matched.some(record => record.meta.requiresAuth) :
          true;
        this.setShowOdds(true); // odds
        mobileWrapper.userLoggedOut();
        notificationProxy.logout();
        if (this.isWrapperApp) {
          this.loaderRemove(document.body);
          this.$router.push({ name: 'home' });
        } else {
          if (routeRequiresAuth) {
            window.location.href = process.env.ROOT_PATH;
          } else {
            window.location.reload();
          }
        }
      } else if (newToken) {
        authorizeApi(newToken);
        this.getUserData();
        this.setShowOdds(false); // trade prices
        mobileWrapper.userLoggedIn(newToken);
        this.connectTimeout = setTimeout(() => {
          clearTimeout(this.connectTimeoutAnonymous);
          notificationProxy.init();
          notificationProxy.authorize(newToken);
          if (notificationProxy.shouldSetupMessaging()) {
            log.info('[MESSAGING] User is opted in. Setting up messaging');
            notificationProxy.setupMessaging();
          } else {
            log.info('[MESSAGING] User is opted out or hasnt made messaging decision');
          }
        }, secondsToDelay * 1000);
        // this.accessTokenRenewal();
      } else {
        this.connectTimeoutAnonymous = setTimeout(() => {
          notificationProxy.init();
        }, secondsToDelay * 1000);
      }
      this.initialised = true;
    },
    windowOnFocusHandler() {
      this.setIsTabActive(true);
    },
    windowOnBlurHandler() {
      this.setIsTabActive(false);
    },
    isTabActiveHandler(value) {
      const secondsToDelay = parseInt(
        process.env.SECONDS_TO_DISCONNECT_DUE_TO_INACTIVITY_WEBSOCKETS, 10);
      if (!secondsToDelay) return;
      // no need to do that on mobile as browser closes tabs there
      if (this.isLayoutMobile) return;
      if (!this.isLoggedIn && !process.env.ANONYMOUS_SIGNALR) return;

      if (value) {
        clearTimeout(this.disconnectTimeout);
      } else {
        this.disconnectTimeout = setTimeout(() => {
          this.setLiveUpdatesDisabled(true);
          notificationProxy.forceDisconnect();
        }, secondsToDelay * 1000);
      }
    },
    optimoveHandler() {
      if (!process.env.OPTIMOVE_ENABLED) {
        return Promise.resolve(1);
      }
      const url = 'https://sdk-cdn.optimove.net/websdk/sdk-v1.0.4.js';
      return loadScript(url, 'optimoveSDK')
        .then((optimoveSDK) => {
          const options = {
            showDimmer: true,
            showWatermark: false,
            reportEventCallback: (response) => {
              if (response.Data) {
                const elem = document.createElement('div');
                elem.innerHTML = response.Data;
                document.body.appendChild(elem);
              }
            },
          };
          optimoveSDK.API.setRealTimeOptions(options);
        });
    },
  },
  watch: {
    token: {
      handler: 'tokenHandler',
      immediate: true,
    },
    isTabActive: {
      handler: 'isTabActiveHandler',
      immediate: true,
    },
  },
  created() {
    this.setupUI();
    this.pwaRedirect();
    authorizeApi(this.token);
    this.getHeaders();
    window.onfocus = this.windowOnFocusHandler;
    window.onblur = this.windowOnBlurHandler;
    window.addEventListener('orientationchange', this.orientationChangeHandler);
    window.addEventListener('resize', this.resizeHandler);
    window.addEventListener('storage', this.storageHandler);
    window.addEventListener('touchstart', this.touchStartHandler, { passive: true });
    window.addEventListener('touchmove', this.touchMoveHandler, { passive: true });
    this.resizeHandler();
    this.initializeUserData();
  },
  beforeDestroy() {
    window.removeEventListener('orientationchange', this.orientationChangeHandler);
    window.removeEventListener('resize', this.resizeHandler);
    window.removeEventListener('storage', this.storageHandler);
    window.removeEventListener('touchstart', this.touchStartHandler);
    window.removeEventListener('touchmove', this.touchMoveHandler);
  },
};



// WEBPACK FOOTER //
// ./src/components/app/app.js